import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  getDayAndMonth,
  getDaysInMonth,
  getNormaleDateOrMonth,
  intlFormatDateShort,
  intlFormatDateWithHour,
} from "../../../../utils/time-utils";
import { EmployeeGroupDTO } from "../../../../models/employee-groups";
import ExportToExcelDialog from "../../ExportToExcelDialog/ExportToExcelDialog";
import { MyTnAContext } from "../../MyTeamPresenceAndLeaves";
import { excelStyles } from "../../utils";
import { exportTnAToExcel } from "../../../../utils/export-presence-and-leaves";
import { LoadResponse } from "../../../../models/api/load";
import { monthsValues } from "../../../CustomDropdowns/CustomDropdownMonthsValues/CustomDropdownMonthsValues";
import { TnABillableTimeRef } from "../../../../models/tna";
import { TnABillableTimeOverviewDTO } from "../../../../models/tna-billable-time-overviews";
import { TnADayDataRef } from "../../../../models/tna/tna-data/tna-day-data-ref";

interface Props {
  disabled: boolean;
  loadEmployeeGroup: () => Promise<EmployeeGroupDTO | undefined>;
  loadTnABillableTimeData: (pageSize?: number) => Promise<LoadResponse<TnABillableTimeOverviewDTO> | undefined>;
  allItems: () => TnABillableTimeOverviewDTO[];
}

const ExportBillableTimeToExcel = ({ disabled, loadEmployeeGroup, loadTnABillableTimeData, allItems }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_billable_time";
  const translationFields = `${tRP}.fields`;
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const {
    legalEntity: { legalEntityOption, selectedLegalEntity, legalEntityQuickFilter },
    employeeGroup: { employeeGroupOption, employeeGroupQuickFilter },
    currentEmployees: { onlyCurrentEmployeesOptionValue, onlyCurrentEmployeesQuickFilter },
    searchingDate: { year, month, yearMonthQuickFilter },
  } = useContext(MyTnAContext);

  const rowsBeforeHeaderInExcel = useCallback(
    (employeeGroup: EmployeeGroupDTO | undefined) => {
      return [
        {
          data: [`Billable time`],
          fontSize: 16,
          isBold: true,
        },
        {
          data: [`Report date ${intlFormatDateWithHour(new Date())}`],
          fontSize: 14,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Legal entities: ${!legalEntityOption?.legalEntityIds?.length ? "All" : selectedLegalEntity?.name ?? ""}`,
          ],
          fontSize: 16,
          isBold: true,
          emptyRowsUnder: 1,
        },
        {
          data: [
            `Employee group: ${
              !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
            }`,
          ],
          fontSize: 12,
          isBold: false,
        },
        {
          data: [
            `Employee status: ${
              onlyCurrentEmployeesOptionValue?.onlyCurrentEmployees ? "Only current employees" : "All employees"
            }`,
          ],
          fontSize: 12,
          isBold: false,
          emptyRowsUnder: 1,
        },
        {
          data: [`Period: ${year} ${t(monthsValues.find((currMonth) => currMonth.value === month)?.key ?? "")}`],
          fontSize: 14,
          isBold: true,
        },
      ];
    },
    [
      legalEntityOption?.legalEntityIds,
      selectedLegalEntity?.name,
      employeeGroupOption,
      onlyCurrentEmployeesOptionValue,
      year,
      month,
    ]
  );

  const headerNames = useMemo(() => {
    const daysInMonth = getDaysInMonth(year, month);

    const dates = [];

    for (let i = 0; i < daysInMonth; i++) {
      dates.push(getDayAndMonth(new Date(year, month, i + 1)));
    }

    return [
      `${t(`${tRPCommon}.employee_code`)}`,
      `${t(`${tRPCommon}.internal_id`)}`,
      `${t(`common_words.employee`)}`,
      `${t(`${tRPCommon}.birth_date`)}`,
      `${t(`common_words.legal_entity`)}`,
      `${t(`common_words.tna_employee_group`)}`,
      `${t(`${translationFields}.ytd`)}`,
      `${t(`${translationFields}.balance`)}`,
      ...dates,
    ];
  }, [year, month]);

  const dataToExportAsExcel = useCallback(
    (allItems: TnABillableTimeOverviewDTO[]) => {
      const daysInMonth = getDaysInMonth(year, month);

      return allItems.map((item) => {
        const dates: { [key: string]: { value: string } } = {};

        const balance = item.daysData
          ?.map((day: TnADayDataRef) => day?.billableTime?.reduce((acc, curr) => acc + curr?.amount, 0))
          ?.reduce((acc: number, curr: number) => acc + curr, 0);

        for (let i = 0; i < daysInMonth; i++) {
          const currDay = item?.daysData?.find((day: TnADayDataRef) => day?.tnADate?.day === i + 1);
          const billableTimes = currDay?.billableTime?.reduce(
            (acc: number, curr: TnABillableTimeRef) => acc + curr?.amount,
            0
          );

          dates[getDayAndMonth(new Date(year, month, i + 1))] = {
            value: !!billableTimes ? `${billableTimes.toFixed(2)}` : " ",
          };
        }

        return {
          [`${t(`${tRPCommon}.employee_code`)}`]: { value: item.employee?.employeeCode ?? " " },
          [`${t(`${tRPCommon}.internal_id`)}`]: { value: item.employee?.internalId ?? " " },
          [`${t(`common_words.employee`)}`]: {
            value: item.employee?.name ?? " ",
            style: { ...excelStyles.employeeCellStyle },
          },
          [`${t(`${tRPCommon}.birth_date`)}`]: {
            value: item.employee?.birthDate ? intlFormatDateShort(item.employee?.birthDate) : " ",
          },
          [`${t(`common_words.legal_entity`)}`]: { value: item.employee?.legalEntity?.name ?? " " },
          [`${t(`common_words.tna_employee_group`)}`]: { value: item?.employee?.tnAGroup?.name ?? " " },
          [`${t(`${translationFields}.ytd`)}`]: {
            value: `${!!item.billableTimeYTD ? item.billableTimeYTD.toFixed(2) : " "}`,
          },
          [`${t(`${translationFields}.balance`)}`]: { value: `${!!balance ? balance.toFixed(2) : " "}` },
          ...dates,
          sortDirection: headerNames,
        };
      });
    },
    [headerNames, year, month]
  );

  const exportToExcel = useCallback(
    async (option: "exportAllData" | "exportCurrent" = "exportCurrent") => {
      if (option === "exportCurrent") {
        const employeeGroup = !!employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        exportTnAToExcel(
          dataToExportAsExcel(allItems()),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${!legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""} ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } Billable time`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }

      if (option === "exportAllData") {
        const employeeGroup = !!employeeGroupOption?.employeeGroupIds?.length ? await loadEmployeeGroup() : undefined;

        const firstResponse = await loadTnABillableTimeData();

        if (!!firstResponse?.error) return;

        const allTnABillableTimeData = await loadTnABillableTimeData(firstResponse?.totalItems);

        if (!!allTnABillableTimeData?.error || !allTnABillableTimeData?.items) return;

        exportTnAToExcel(
          dataToExportAsExcel(allTnABillableTimeData.items),
          headerNames as string[],
          `${new Date().getFullYear()} ${getNormaleDateOrMonth(new Date().getMonth() + 1)} ${getNormaleDateOrMonth(
            new Date().getDate()
          )} ${!legalEntityOption?.legalEntityIds?.length ? "All legal entities" : selectedLegalEntity?.name ?? ""} ${
            !employeeGroupOption?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
          } Billable time`,
          rowsBeforeHeaderInExcel(employeeGroup)
        );
      }
    },
    [
      rowsBeforeHeaderInExcel,
      loadEmployeeGroup,
      headerNames,
      dataToExportAsExcel,
      employeeGroupOption?.employeeGroupIds,
      loadTnABillableTimeData,
    ]
  );

  return (
    <ExportToExcelDialog
      handleExportToExcel={exportToExcel}
      disabled={disabled}
    />
  );
};

export default ExportBillableTimeToExcel;
